import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Skeleton,
} from "@mui/material";
import axios from "axios";
import {
  FormLabel,
  ModalError,
  ModalSuccess,
  NumberInput,
  PasswordInput,
  PrimaryButton,
  SecondaryButton,
  TextInput,
} from "components";
import { useEffect, useState } from "react";
import { hardBaseUrl } from "services/urlConstant";
import { getErrors, validateEmail } from "utils";

const ModalFormParameterAssociation = ({ open, onClose, preload, getData }) => {
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const [loadingPage, setLoadingPage] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const [payload, setPayload] = useState({
    email: "",
    name: "",
    phone_number: "",
    pic: "",
    association_code: "",
    ...(!preload && { password: "", re_password: "" }),
  });

  const handleChangePayload = (value, key) => {
    setPayload(currentState => ({
      ...currentState,
      [key]: value,
    }));
    if (!preload && (key === "password" || key === "confirm_password")) {
      setPasswordError(null);
    }
    if (key === "email") {
      setEmailError(null);
    }
  };

  const getDetail = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(
        `${hardBaseUrl}/association/detail/${preload}`,
        {
          headers,
        }
      );
      setPayload(res?.data?.data);
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
    } finally {
      setLoadingPage(false);
    }
  };
  const handleSubmit = async () => {
    const method = preload ? axios.put : axios.post;
    const {
      password,
      re_password,
      association_id,
      email,
      ...restPayload
    } = payload;
    const modifiedPayload = {
      ...restPayload,
      email,
      ...(!preload && { password, re_password }),
    };
    let hasError = false;
    setPasswordError(null);
    setEmailError(null);

    if (!preload && password !== re_password) {
      setPasswordError("Password and Confirm Password does not match");
      hasError = true;
    }

    if (!validateEmail(email)) {
      setEmailError(
        "Invalid email, please enter your email address in format: yourname@example.com"
      );
      hasError = true;
    }

    if (hasError) return;

    try {
      setLoadingButton(true);
      await method(
        `${hardBaseUrl}/association/${
          preload ? `update/${association_id}` : "create"
        }`,
        modifiedPayload,
        { headers }
      );
      onClose();
      ModalSuccess(
        `Successfully ${preload ? "Update" : "Create"} Association`
      ).then(() => {
        setPayload({});
        getData();
      });
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
    } finally {
      setLoadingButton(false);
    }
  };

  useEffect(() => {
    if (preload) {
      getDetail();
    }
  }, [preload]);

  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <DialogTitle>{`${preload ? "Edit" : "Add"} Association`}</DialogTitle>
      <DialogContent dividers>
        {loadingPage ? (
          <Grid container direction="column" rowSpacing={2}>
            {Array.from({ length: 5 }).map((_, index) => (
              <Grid item key={index}>
                <Box mb="8px">
                  <Skeleton variant="rounded" width="40%" height={25} />
                </Box>
                <Skeleton variant="rounded" width="100%" height={30} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container direction="column" rowSpacing={1}>
            {MockFieldForm({ preload, passwordError, emailError })?.map(
              ({ title, key, type, error, optional }, index) => (
                <Grid item key={index}>
                  <FormLabel label={title} required={!optional} />
                  {type === "password" ? (
                    <PasswordInput
                      placeholder={title}
                      autoComplete={`new-${key}`}
                      value={payload?.[key]}
                      onChange={e => handleChangePayload(e?.target?.value, key)}
                      error={!!error}
                      helperText={error}
                      disabled={loadingButton}
                    />
                  ) : type === "phone" ? (
                    <NumberInput
                      placeholder={title}
                      autoComplete={`new-${key}`}
                      value={payload?.[key]}
                      onChange={e => handleChangePayload(e, key)}
                      disabled={loadingButton}
                    />
                  ) : (
                    <TextInput
                      placeholder={title}
                      value={payload?.[key]}
                      autoComplete={`new-${key}`}
                      onChange={e => handleChangePayload(e?.target?.value, key)}
                      error={!!error}
                      helperText={error}
                      disabled={loadingButton}
                    />
                  )}
                </Grid>
              )
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <SecondaryButton
          label="Cancel"
          disabled={loadingPage}
          onClick={onClose}
        />
        <PrimaryButton
          label={loadingButton ? "Saving" : "Save"}
          loading={loadingButton}
          disabled={loadingPage || loadingButton}
          onClick={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};

const MockFieldForm = ({ preload, passwordError, emailError }) => [
  {
    title: "Association Name",
    key: "name",
  },
  {
    title: "Transmitter Code",
    key: "association_code",
    optional: true,
  },
  {
    title: "PIC",
    key: "pic",
  },
  {
    title: "Email",
    key: "email",
    error: emailError,
  },
  {
    title: "Phone Number",
    key: "phone_number",
    type: "phone",
  },
  ...(preload
    ? []
    : [
        {
          title: "Password",
          key: "password",
          type: "password",
          error: passwordError,
        },
        {
          title: "Confirm Password",
          key: "re_password",
          type: "password",
          error: passwordError,
        },
      ]),
];
export default ModalFormParameterAssociation;
